/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled'
import { getRenderingAndBilling } from 'components/lab/billingCharge/utils'
import * as generalHelper from 'helpers/generalHelper'
import React, { useState } from 'react'
import { Button } from 'react-bootstrap'
import { useGetPatientTransactionsQuery } from 'store/services/patientTransactions'
import { useDebounce } from 'use-debounce'
import { usePrintLedger } from './usePrintLedger'
import { formatCalendarDate, formatEasternDate } from 'helpers/dates'

const Container = styled.div`
  width: 100%;
  padding: 10px;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`

const Title = styled.div`
  font-weight: 600;
  font-size: 18px;
`

const SearchInput = styled.input`
  width: 260px;
  padding: 0.5rem;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }
`

const EmptyState = styled.div`
  padding: 2rem;
  text-align: center;
  color: #6b7280;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;

  @media print {
    font-size: 0.75rem;
  }
`

const TableHeader = styled.thead`
  background-color: #f9fafb;
  border-bottom: 2px solid #e5e7eb;
`

const TableHeaderCell = styled.th`
  text-align: left;
  padding: 0.625rem 0.5rem;
  font-weight: 600;
  font-size: 0.8125rem;
  color: #374151;
`

const TableBody = styled.tbody``

const TableRow = styled.tr<{ level?: number }>`
  border-bottom: 1px solid ${(props) => (props.level === 2 ? '#f3f4f6' : '#e5e7eb')};
  background-color: ${(props) => (props.level === 1 ? '#f9fafb' : '#ffffff')};

  @media print {
    page-break-inside: avoid;
  }
`

const TableCell = styled.td<{ level?: number }>`
  padding: ${(props) => (props.level === 1 ? '0.5rem 0.5rem' : '0.375rem 0.5rem')};
  color: #1f2937;
  font-weight: ${(props) => (props.level === 1 ? 500 : 400)};
  font-size: ${(props) => (props.level === 1 ? '0.8125rem' : '0.75rem')};

  @media print {
    padding: 0.25rem 0.25rem;
  }
`

interface Props {
  patientId?: string
  patientName?: string
}

const initialSearch = {
  search: '',
}

const formatDate = (date: string | null) => formatEasternDate(date, '--')

const currency = (v?: number | null) => (typeof v === 'number' ? `$${v.toFixed(2)}` : '--')

const getTypeLabel = (type: string) => {
  const labels: Record<string, string> = {
    CHARGE: 'Charge',
    CHARGE_SUBMITTED: 'Charge Submitted',
    CHARGE_DELETED: 'Charge Deleted',
    PAYMENT: 'Payment',
    PAYMENT_APPLIED: 'Payment Applied',
    TRANSFER: 'Transfer',
    PRE_PAYMENT: 'Pre-Payment',
    ADJUSTMENT: 'Adjustment',
    WRITE_OFF: 'Write-Off',
    REFUND: 'Refund',
    BALANCE_UPDATED: 'Balance Updated',
  }
  return labels[type] || type
}

export const PatientTransactionsListing = ({ patientId, patientName }: Props) => {
  const [search, setSearch] = useState(initialSearch)
  const [debouncedSearch] = useDebounce(search, 500)

  const { printLedger } = usePrintLedger()

  const { data: transactionsData, isLoading } = useGetPatientTransactionsQuery({
    patientId: Number(patientId),
    ...generalHelper.cleanObj(debouncedSearch, true),
  })

  const transactions = transactionsData?.items || []

  const changeSearch = (field: keyof typeof initialSearch, value: string) => {
    setSearch((p) => ({ ...p, [field]: value }))
  }

  if (isLoading) {
    return (
      <Container>
        <EmptyState>Loading...</EmptyState>
      </Container>
    )
  }

  return (
    <Container>
      <Header>
        <div className={'d-flex align-items-center'}>
          <Title>Ledger</Title>
          <Button
            variant="outline-secondary"
            onClick={() => printLedger(patientName)}
            disabled={isLoading}
            className="no-print ml-2">
            <i className={'fas fa-print mr-2'} /> Print
          </Button>
        </div>
        <div className={'d-flex'}>
          <SearchInput
            placeholder="Search..."
            value={search.search}
            onChange={(e) => changeSearch('search', e.target.value)}
          />
        </div>
      </Header>

      <div data-print-content>
        {transactions.length === 0 ? (
          <EmptyState>No transactions found</EmptyState>
        ) : (
          <Table>
            <TableHeader>
              <tr>
                <TableHeaderCell>Date</TableHeaderCell>
                <TableHeaderCell>Type</TableHeaderCell>
                <TableHeaderCell>Procedure / Details</TableHeaderCell>
                <TableHeaderCell>Amount</TableHeaderCell>
                <TableHeaderCell>Payer / Method</TableHeaderCell>
                <TableHeaderCell>Responsible</TableHeaderCell>
              </tr>
            </TableHeader>
            <TableBody>
              {transactions.map((transaction) => {
                return (
                  <React.Fragment key={transaction.id}>
                    <TableRow level={1}>
                      <TableCell level={1}>{formatDate(transaction.createdAt)}</TableCell>
                      <TableCell level={1}>{getTypeLabel(transaction.type)}</TableCell>
                      <TableCell level={1} colSpan={4}>
                        {transaction.type === 'BALANCE_UPDATED' && transaction.description
                          ? transaction.description
                          : getRenderingAndBilling(transaction.renderingAndBilling)}
                      </TableCell>
                    </TableRow>
                    <TableRow level={2}>
                      <TableCell level={2}></TableCell>
                      <TableCell level={2}>{transaction.cptCodes?.join(', ') || '--'}</TableCell>
                      <TableCell level={2}>
                        {transaction.startDos
                          ? `DoS: ${formatCalendarDate(transaction.startDos, '--')}${transaction.endDos && transaction.endDos !== transaction.startDos ? ` - ${formatCalendarDate(transaction.endDos, '--')}` : ''}`
                          : '--'}
                      </TableCell>
                      <TableCell level={2}>{currency(transaction.amount)}</TableCell>
                      <TableCell level={2}>
                        {transaction.paymentMethod || transaction.billingPrimary || '--'}
                      </TableCell>
                      <TableCell level={2}>{transaction.responsible || '--'}</TableCell>
                    </TableRow>
                  </React.Fragment>
                )
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </Container>
  )
}

export default PatientTransactionsListing
